import { PlusOutlined } from '@ant-design/icons';
import { CustomersAiIcon } from '@mm/icons';
import { ConnectionModel } from '@mm/state/model/ConnectionModel';
import { selectShopifyConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { ContactsSync } from '../ContactsSync/ContactsSync';
import { IntegrationRevenue } from '../IntegrationRevenue/IntegrationRevenue';
import { KlaviyoErrorMessage } from '../KlaviyoIntegrations/KlaviyoErrorMessage';
import { useManageConnection } from '../KlaviyoIntegrations/use-manage-connection';
import { OauthTokenConnection } from '../OauthTokenConnection/OauthTokenConnection';
import { ShopifyConnection } from './ShopifyConnection';
import styles from './ShopifyIntegrations.module.css';
import { useConnectShopify } from './use-connect-shopify';

export const ShopifySettings = (props: {
  connectToken: (tokenId?: number) => void;
  connectionsMessage?: string | null;
}) => {
  const { connectToken, connectionsMessage } = props;
  const availableTags = useSelector((state: StoreState) =>
    tagsOfKinds(state, ['regular']),
  );
  const revenue = useSelector((state: StoreState) => state.shopify.revenue);

  const shopifyConnections = useSelector(selectShopifyConnections);
  const connection = shopifyConnections[0];
  const accounts = useSelector((state: StoreState) => state.shopify.accounts);
  const audiences = useSelector(selectAudienceSorted);
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const helpLink =
    'https://customers.ai/help/l/en/article/ehihg7mr8f-how-to-configure-your-shopify-custom-api';

  const matchingToken = accounts.find(
    token => token.id === connection?.accessToken?.id,
  );
  const isReadOnly = matchingToken?.readOnly;

  const connectionTemplate = useCallback(
    (extra: Partial<ConnectionModel> = {}): Partial<ConnectionModel> => ({
      active: false,
      name: 'Shopify Connection',
      connectionKind: 'shopify',
      ...(matchingToken?.id && {
        shopifyParams: {
          tokenId: matchingToken.id,
          tokenType: matchingToken.type,
        },
      }),
      ...extra,
    }),
    [matchingToken],
  );

  const validConnection = (
    isAllContactsSync: boolean,
    connection: Partial<ConnectionModel>,
  ) =>
    Boolean(
      !isAllContactsSync ? connection.audienceId : !connection.audienceId,
    );

  const {
    allConnections,
    isAllContactsSync,
    selectableAudiences,
    addConnection,
    remove,
    updateOrEdit,
    onChangeType,
  } = useManageConnection({
    connections: shopifyConnections,
    audiences,
    validConnection,
    connectionTemplate,
  });

  const { revoke, getRevenues, revokeAccessToken } = useConnectShopify();
  const oauthToken = matchingToken?.type === 'Shopify::OauthToken';

  const multipleConnections = allConnections.length > 1;
  const canAdd = selectableAudiences().length;

  const error =
    (isAllContactsSync || connection?.audienceId) && connectionsMessage
      ? connectionsMessage
      : !isAllContactsSync && !connection?.audienceId
      ? 'Please, choose an audience to sync to Shopify'
      : '';

  return (
    <div className={styles.settingsContent}>
      <IntegrationRevenue
        platformName="Shopify"
        totalRevenue={revenue.totalSpent}
        totalOrders={revenue.ordersCount}
        getRevenues={getRevenues}
        facebookPageId={activeFbPageId}
        advanced={false}
      />
      <div className={classNames({ [styles.boxDisabled]: isReadOnly })}>
        <OauthTokenConnection
          helpLink={helpLink}
          controlsDisabled={!!isReadOnly}
          token={matchingToken}
          connectToken={connectToken}
          revoke={oauthToken ? revoke : revokeAccessToken}
          connectedTooltipMsg={matchingToken?.shop}
        />
      </div>

      {matchingToken && (
        <ContactsSync
          connection={connection}
          isReadOnly={!!isReadOnly}
          allConnections={shopifyConnections}
          onRadioChange={onChangeType}
          name="Shopify"
          withSwitch
        >
          {error && (
            <KlaviyoErrorMessage
              classNames={styles.syncWarningBox}
              error={error}
            />
          )}
          {isAllContactsSync && (
            <div className={styles.gridContainer}>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                Add tags to contacts in Shopify
              </div>
              <ShopifyConnection
                className={styles.gridAudiences}
                key={connection.id}
                canDelete={false}
                connection={connection}
                audiences={selectableAudiences(connection)}
                update={props => updateOrEdit(0, props)}
                remove={() => remove(0)}
                activeFbPageId={activeFbPageId}
                availableTags={availableTags}
                onlyTags
              />
            </div>
          )}
          {!isAllContactsSync && (
            <div className={styles.gridContainer}>
              <div className={styles.gridAudiences}>
                <div className={classNames(styles.gridColumn, styles.firstRow)}>
                  <CustomersAiIcon className={styles.gridIcon} /> Customers.ai
                  Audience
                </div>
                <div className={classNames(styles.gridColumn, styles.firstRow)}>
                  Add tags to contacts in Shopify
                </div>
                <div className={classNames(styles.gridColumn, styles.firstRow)}>
                  Actions
                </div>
              </div>

              {allConnections.map((connection, idx) => (
                <ShopifyConnection
                  className={styles.gridAudiences}
                  key={`connection-${idx}`}
                  canDelete={multipleConnections}
                  connection={connection}
                  audiences={selectableAudiences(connection)}
                  update={props => updateOrEdit(idx, props)}
                  remove={() => remove(idx)}
                  activeFbPageId={activeFbPageId}
                  availableTags={availableTags}
                />
              ))}

              {canAdd ? (
                <PrimaryButton
                  className={classNames(
                    'integrations__header-add-button-secondary',
                    'integrations__header-add-button-action',
                    'integrations__header-button-no-margin',
                  )}
                  onClick={addConnection}
                  leftIcon={<PlusOutlined />}
                >
                  Add
                </PrimaryButton>
              ) : null}
            </div>
          )}
        </ContactsSync>
      )}
    </div>
  );
};
